import { Formik } from 'formik';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { date, number, object } from 'yup';
import { PrimaryButton } from '../../../../components/Buttons/PrimaryButton';
import { SecondaryButton } from '../../../../components/Buttons/SecondaryButton';
import { Form, FormInputControl, FormMobisPicker } from '../../../../components/forms';
import { CenteredLoadingIndicator } from '../../../../components/LoadingIndicator';
import ServerValidationBox from '../../../../components/ServerValidationBox';
import { ButtonSizeEnum } from '../../../../constants';
import { HeightEnum, PHRTrackerLengthDTO } from '../../../../generated';
import { createForPhrSection, updateForPhrSection } from '../../../../store/root-creator';
import { AppState } from '../../../../store/root-reducers';
import { ReduxPHRCategoryEnum } from '../../../../store/types';
import { convertHeight } from '../../../../utils/conversionHelpers';
import { ButtonRow } from '../../components/StyledPhrItems';

interface IProps {
  selectedData: PHRTrackerLengthDTO | null;
  patientID: string;
  toggleView: () => void;
  unit: HeightEnum;
  unitSelector: any;
}

interface IForm {
  trackerDate: moment.Moment;
  length?: number;
}

const LengthForm = ({ selectedData, patientID, toggleView, unit, unitSelector }: IProps) => {
  const dispatch = useDispatch();
  const { error, isPhrLoading } = useSelector((state: AppState) => state.phrPatientState);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);

  const onSubmit = async (values: IForm) => {
    const obj = Object.assign({}, values);
    obj.length = convertHeight(false, values.length, HeightEnum.Cm, unit);
    setSubmitting(true);
    if (selectedData) {
      selectedData.trackerDate = moment(obj.trackerDate).toDate();
      selectedData.length = obj.length;
      await dispatch(
        updateForPhrSection(ReduxPHRCategoryEnum.PHRLengths, selectedData, patientID, true),
      );
    } else {
      await dispatch(createForPhrSection(ReduxPHRCategoryEnum.PHRLengths, obj, patientID, true));
    }
  };

  const handleUnitChange = (setFieldValue, length, toUnit) => {
    setFieldValue('length', convertHeight(true, length, toUnit, unit));
  };

  useEffect(() => {
    if (isSubmitting && !isPhrLoading) {
      if (error) {
        setSubmitting(false);
      } else {
        toggleView();
      }
    }
  }, [error, isPhrLoading, isSubmitting, toggleView]);

  const initialValues: IForm = {
    trackerDate: selectedData ? moment(selectedData.trackerDate!) : moment(),
    length: selectedData ? selectedData.length : undefined,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={object({
        trackerDate: date().required('Date is required').typeError('Invalid date'),
        length: number()
          .required('Length is required')
          .positive('Length must be a postive number')
          .typeError('Length must be a postive number')
          .moreThan(0, 'Value must be greater than 0')
          .lessThan(300, 'Value must be less than 300'),
      })}
      onSubmit={onSubmit}
    >
      {({ status, setFieldValue, values }) => (
        <Form error={error}>
          {isSubmitting ? (
            <CenteredLoadingIndicator />
          ) : (
            <>
              <ServerValidationBox message={status} />
              <div className="row">
                <div className="col-12 d-flex justify-content-end">
                  {unitSelector((x) => {
                    handleUnitChange(setFieldValue, values.length, x);
                  })}
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-sm-6">
                  <FormMobisPicker
                    name="trackerDate"
                    label="Tracked at"
                    controls={['calendar', 'time']}
                    max={moment().toDate()}
                    min={moment().add(-150, 'year').toDate()}
                  />
                  {/* <FormDateTimePicker
                    name="trackerDate"
                    label="Tracked at"
                    maxDate={moment().toDate()}
                    minDate={moment().add(-150, 'year').toDate()}
                  /> */}
                </div>
              </div>
              <div className="row">
                <div className="col-12 col-sm-6">
                  <FormInputControl name="length" label="Length" type="text" />
                </div>
              </div>
              <ButtonRow>
                <SecondaryButton size={ButtonSizeEnum.medium} onClick={toggleView}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton size={ButtonSizeEnum.medium} type="submit">
                  Save
                </PrimaryButton>
              </ButtonRow>
            </>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default LengthForm;
